import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BarChartIcon from "@mui/icons-material/BarChart";
import SearchIcon from "@mui/icons-material/Search";
import { uploadCapWeek, getCapApplyStatistics } from "../api/capApi";

const SECTION_COLS = [
    { key: "rx", label: "Rx" },
    { key: "intermediate", label: "Int" },
    { key: "beginner", label: "Beg" },
    { key: "skillWork", label: "Skill" },
    { key: "stretching", label: "Stretch" },
    { key: "atHome", label: "At Home" },
    { key: "workYourWeakness", label: "WYW" },
    { key: "compete", label: "Compete" },
];

const defaultStart = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
};
const defaultEnd = () => new Date().toISOString().slice(0, 10);

export default function CapUploadPanel() {
    const [affiliateFile, setAffiliateFile] = useState(null);
    const [classPlanFile, setClassPlanFile] = useState(null);
    const [competeFile, setCompeteFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [status, setStatus] = useState({ type: "", message: "" });
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    const fetchStats = async () => {
        setStatsLoading(true);
        setStats(null);
        try {
            const data = await getCapApplyStatistics({ startDate, endDate });
            setStats(data);
        } catch {
            setStats(null);
        } finally {
            setStatsLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!affiliateFile || !classPlanFile) {
            setStatus({ type: "error", message: "Please select both CAP files." });
            return;
        }

        setIsUploading(true);
        setStatus({ type: "", message: "" });
        try {
            const response = await uploadCapWeek(affiliateFile, classPlanFile, competeFile || undefined);
            setStatus({
                type: "success",
                message: response?.message || "CAP week uploaded successfully.",
            });
            setAffiliateFile(null);
            setClassPlanFile(null);
            setCompeteFile(null);
        } catch (error) {
            setStatus({
                type: "error",
                message: error?.response?.data?.error || "Failed to upload CAP files.",
            });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Card sx={{ maxWidth: 860, mx: "auto", borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                    CAP Upload
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    Upload weekly CAP files (`CAP - Affiliate` and `CAP_class_plan`) to auto-refresh CAP view for everyone.
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            CAP Affiliate CSV
                        </Typography>
                        <Button variant="outlined" component="label">
                            Choose File
                            <input
                                hidden
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => setAffiliateFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {affiliateFile ? affiliateFile.name : "No file selected"}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            CAP Class Plan CSV
                        </Typography>
                        <Button variant="outlined" component="label">
                            Choose File
                            <input
                                hidden
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => setClassPlanFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {classPlanFile ? classPlanFile.name : "No file selected"}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            CAP Compete CSV (optional)
                        </Typography>
                        <Button variant="outlined" component="label">
                            Choose File
                            <input
                                hidden
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => setCompeteFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {competeFile ? competeFile.name : "No file selected"}
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleUpload}
                        disabled={isUploading}
                        startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    >
                        {isUploading ? "Uploading..." : "Upload CAP Week"}
                    </Button>

                    {status.message ? (
                        <Alert severity={status.type || "info"}>
                            {status.message}
                        </Alert>
                    ) : null}
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <BarChartIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        Apply Statistics
                    </Typography>
                </Box>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Number of workouts logged across each option by affiliate (each Apply click = 1 record)
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="From"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ minWidth: 160 }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ minWidth: 160 }}
                    />
                    <Button
                        variant="outlined"
                        onClick={fetchStats}
                        disabled={statsLoading}
                        startIcon={statsLoading ? <CircularProgress size={18} /> : <SearchIcon />}
                    >
                        {statsLoading ? "Loading..." : "Load"}
                    </Button>
                </Stack>

                {statsLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={32} />
                    </Box>
                ) : stats && (stats.byAffiliate || []).length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Affiliate</TableCell>
                                    {SECTION_COLS.map(({ key, label }) => (
                                        <TableCell key={key} align="center" sx={{ fontWeight: 600 }}>
                                            {label}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        Total
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(stats.byAffiliate || []).map((aff) => (
                                    <TableRow key={aff.affiliateId} hover>
                                        <TableCell>{aff.affiliateName}</TableCell>
                                        {SECTION_COLS.map(({ key }) => (
                                            <TableCell key={key} align="center">
                                                {aff.bySection?.[key] || 0}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">{aff.total}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: "grey.100" }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                    {SECTION_COLS.map(({ key }) => {
                                        const sum = (stats.byAffiliate || []).reduce(
                                            (a, aff) => a + (aff.bySection?.[key] || 0),
                                            0
                                        );
                                        return (
                                            <TableCell key={key} align="center" sx={{ fontWeight: 600 }}>
                                                {sum}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {stats.total}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : stats ? (
                    <Typography variant="body2" color="text.secondary">
                        No data for selected period.
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Select period and click Load.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}
